import { Overlay, type OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, Injector, isDevMode } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { SettingsConfirmDialogComponent } from './settings-confirm-dialog.component';
import { SETTINGS_CONFIRMATION, type SettingsConfirmation } from './settings-confirm.types';

@Injectable()
export class SettingsConfirmService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly overlay = inject(Overlay);
  private overlayRef: OverlayRef | undefined;
  private cancelPending: (() => void) | undefined;

  constructor() {
    this.destroyRef.onDestroy(() => this.cancelPending?.());
  }

  public confirm(confirmation: SettingsConfirmation, signal?: AbortSignal): Observable<boolean> {
    const result = new ReplaySubject<boolean>(1);
    if (signal?.aborted) {
      result.next(false);
      result.complete();
      return result;
    }
    if (this.overlayRef) {
      if (isDevMode()) {
        console.error('Settings confirmation depth is limited to one.');
      }
      result.next(false);
      result.complete();
      return result;
    }

    const opener = this.document.activeElement instanceof HTMLElement ? this.document.activeElement : undefined;
    const windowPane = this.document.querySelector<HTMLElement>('.settings-window-overlay');
    windowPane?.setAttribute('inert', '');
    const injector = Injector.create({
      parent: this.injector,
      providers: [{ provide: SETTINGS_CONFIRMATION, useValue: confirmation }],
    });
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'settings-confirm-backdrop',
      panelClass: 'settings-confirm-overlay',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });
    const component = this.overlayRef.attach(new ComponentPortal(SettingsConfirmDialogComponent, null, injector));
    component.changeDetectorRef.detectChanges();
    let finished = false;
    const finish = (confirmed: boolean): void => {
      if (finished) return;
      finished = true;
      signal?.removeEventListener('abort', cancel);
      this.cancelPending = undefined;
      this.dispose();
      result.next(confirmed);
      result.complete();
      opener?.focus();
    };
    const cancel = (): void => finish(false);
    this.cancelPending = cancel;
    component.instance.cancel.subscribe(() => finish(false));
    component.instance.confirm.subscribe(() => finish(true));
    this.overlayRef.backdropClick().subscribe(() => finish(false));
    if (signal?.aborted) finish(false);
    else signal?.addEventListener('abort', cancel, { once: true });
    return result;
  }

  private dispose(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.document.querySelector<HTMLElement>('.settings-window-overlay')?.removeAttribute('inert');
  }
}
